import { createSelector } from 'reselect';
import { allConditionsCheckedSelector } from '../stock/selectors';
import { calculateMinOrderSum, calculatePickupPricingTotals, calculatePricing } from './calculator';
import { summarizeComposition } from './classifiers';
import { buildProductTypeDiscountSettings } from './compositionSettings';

export const minOrderSumSelector = createSelector(
  [
    state => state.order.checkout.currentRestaurant?.min_sum,
    state => state.stock.data.details?.min_order_sum,
    state => state.stock.isApplied,
    allConditionsCheckedSelector,
  ],
  (minOrderSum, minStockSum, stockIsApplied, stockIsChecked) =>
    calculateMinOrderSum({ minOrderSum, minStockSum, stockIsApplied, stockIsChecked }),
);

const pricingInputSelector = createSelector(
  [
    state => state.order.checkout.deliveryType,
    state => state.cart?.sailplay_discount || 0,
    state => state.stock.data.code,
    state => state.cart.price,
    state => state.cart.discount_price,
    state => state.stock.unconditionalList.data.rewards?.[0] || {},
    state => state.cart.composition,
    state => state.user.profile.loyalty?.points?.confirmed || 0,
    state => state.city.userCity.sailplay?.discount_percent || 100,
    state => state.city.userCity.sailplay?.max_points_spend,
    minOrderSumSelector,
  ],
  (
    deliveryType,
    usedBonuses,
    stockCode,
    price,
    discountPrice,
    pickupBonuses,
    composition,
    loyaltyBalance,
    discountPercent,
    maxPointsSpend,
    minOrderSum,
  ) => ({
    deliveryType,
    usedBonuses,
    stockCode,
    price,
    discountPrice,
    pickupBonuses,
    composition,
    loyaltyBalance,
    discountPercent,
    maxPointsSpend,
    minOrderSum,
  }),
);

export const cartPricingSelector = createSelector([pricingInputSelector], calculatePricing);

export const priceIncludingBonusesSelector = createSelector(
  [cartPricingSelector],
  pricing => pricing.total,
);

export const maxCashbackCanUseSelector = createSelector(
  [cartPricingSelector],
  pricing => pricing.maxCashbackCanUse,
);

export const maxSpendableBonusesSelector = createSelector(
  [cartPricingSelector],
  pricing => pricing.maxSpendableBonuses,
);

export const giftsForBonusesSumSelector = createSelector(
  [cartPricingSelector],
  pricing => pricing.giftsForBonusesSum,
);

export const unusedBonusesSum = createSelector(
  [cartPricingSelector],
  pricing => pricing.unusedBonuses,
);

export const maxBonusesMinusGiftsSelector = createSelector(
  [cartPricingSelector],
  pricing => pricing.maxBonusesMinusGifts,
);

export const eligibleForBonusesTotalSelector = createSelector(
  [cartPricingSelector],
  pricing => pricing.eligibleForBonusesTotal,
);

export const compositionSettingsSelector = createSelector(
  [state => state.cart?.composition, state => state.stock.data.code],
  (composition, stockCode) => {
    const compositionSummary = summarizeComposition(composition);
    const productTypeSettings = buildProductTypeDiscountSettings(compositionSummary, {
      stockCode,
    });

    return {
      compositionSummary,
      productTypeSettings,
    };
  },
);

export const pickupPricingTotalsSelector = createSelector(
  [
    state => state.cart?.sailplay_discount || 0,
    state => state.stock.data.code,
    state => state.cart.price,
    state => state.stock.unconditionalList.data.rewards?.[0] || {},
    state => state.cart.composition,
  ],
  (usedBonuses, stockCode, price, pickupBonuses, composition) =>
    calculatePickupPricingTotals({
      usedBonuses,
      stockCode,
      price,
      pickupBonuses,
      composition,
    }),
);
